import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  untracked,
  viewChild,
  DOCUMENT,
  EventEmitter,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  type Config,
  type Data,
  type Layout,
  PlotlyHTMLElement,
  Plots,
  newPlot,
  redraw,
  react,
} from 'plotly.js-basic-dist-min';
import { delay, Subject } from 'rxjs';

@Component({
  selector: 'app-plotly-plot',
  templateUrl: './plotly-plot.component.html',
  styleUrls: ['./plotly-plot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlotlyPlotComponent implements OnDestroy {
  private destroyRef = inject(DestroyRef);
  private window = inject(DOCUMENT).defaultView;

  readonly data = input.required<Data[]>();
  readonly layout = input.required<Partial<Layout>>();
  readonly config = input.required<Partial<Config>>();
  readonly resizeSubject = input<Subject<void>>(undefined);

  readonly plotEl = output<PlotlyHTMLElement>();
  private plotly: PlotlyHTMLElement;
  private resizer: ResizeObserver;
  @Output() plotlyClick = new EventEmitter<any>();
  private readonly plotDiv = viewChild<ElementRef<HTMLDivElement>>('plot');

  constructor() {
    effect(() => {
      const div = this.plotDiv().nativeElement;
      if (!div) return;

      const data = this.data();
      const layout = this.layout();
      const config = this.config();

      untracked(async () => {
        try {
          if (!this.plotly) {
            this.plotly = await newPlot(div, data, layout, config);
            this.plotEl.emit(this.plotly);
            this.resizer = new ResizeObserver(() => {
              requestAnimationFrame(() => {
                if (this.plotly) {
                  Plots.resize(this.plotly);
                }
              });
            });

            if (div.parentElement) {
              this.resizer.observe(div.parentElement);
            }

            this.plotEl.emit(this.plotly);

            this.plotly.on('plotly_click', (event: any) => {
              this.plotlyClick.emit(event);
            });

            this.window.onresize = () => Plots.resize(this.plotly);
          } else {
            await react(this.plotly, data, layout, config);
          }
        } catch (_) {
          console.log(_);
        }
      });
    });
  }
  ngOnDestroy(): void {
    this.window.onresize = undefined;
    this.resizer?.disconnect();
    if (this.plotly) {
      this.plotly.removeAllListeners('plotly_click');
    }
  }
}
